// /src/components/WorkspaceSidebar.tsx

// What? A component for the workspace sidebar.
// Why? To display the workspace sidebar with navigation items.
// How?
// - Uses the Box component from MUI Joy to create a flexible container for the sidebar
// - Implements a collapsible sidebar using React state (isCollapsed) and CSS classes
// - Renders a toggle button to expand/collapse the sidebar for better space management
// - Utilizes the List component to structure the navigation items in a vertical layout
// - Employs ListItem and ListItemButton components to create interactive navigation elements
// - Integrates icons (e.g., HomeIcon, ArrowBackIcon) to visually represent each navigation item
// - Leverages React Router's useParams, useLocation, and useNavigate hooks for dynamic routing
// - Applies conditional CSS classes to highlight the active navigation item based on the current route
// This implementation creates a responsive, collapsible sidebar that provides easy navigation
// within the workspace, fulfilling the component's purpose of displaying an interactive
// and user-friendly workspace sidebar.

import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/joy";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { useLocation, useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../providers/WorkspaceProvider";
import { useFeatureFlagsContext } from "../providers/FeatureFlagProvider";
import { Link } from "react-router-dom";
import { moduleRegistry } from "../modules/moduleRegistry";
import { useTutorialTarget, useTutorialHint, useTutorial } from "./Tutorial";

interface WorkspaceSidebarProps {
  isCollapsed: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  isCollapsed,
  isMobile = false,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspace, isOwner, isAdmin, isEditor, isUser, enabledModules } =
    useWorkspaceContext();
  const { flags } = useFeatureFlagsContext();
  const { resetAllDismissed } = useTutorial();

  // Source from per-tab context (URL-derived), NOT localStorage. localStorage
  // is shared across browser tabs, so reading the workspace name from there
  // made the sidebar in one tab display whichever workspace last loaded in
  // any other tab. The context value is partitioned by react-router's
  // workspace_id param and stays correct per-tab.
  const workspaceName = workspace?.name;

  // Cross-device: derived from workspace data (correct after page load on any device).
  const workspaceIsReady = !!(
    workspace?.description?.trim() || workspace?.assistant_instructions?.trim()
  );
  // Same-session: set immediately when the onboarding background job fires,
  // before refreshWorkspace() has had a chance to re-fetch updated workspace data.
  const [sessionIsReady, setSessionIsReady] = useState(false);
  const isReady = workspaceIsReady || sessionIsReady;

  // The setup-chat "ready" gate is an IDA onboarding concept (it's IDA's setup
  // conversation that populates the workspace description / assistant
  // instructions). It must not lock navigation in workspaces without IDA.
  const settingsLocked = enabledModules.includes("ida") && !isReady;

  // Reset the session flag when navigating to a different workspace.
  useEffect(() => {
    setSessionIsReady(false);
  }, [workspace?.id]);

  // Clear the session flag when the workspace is explicitly reset (dev tool).
  useEffect(() => {
    if (!workspaceIsReady) setSessionIsReady(false);
  }, [workspaceIsReady]);

  // Unlock immediately when Chat signals onboarding complete (same tab, same session).
  useEffect(() => {
    if (!workspace?.id) return;
    const wid = workspace.id;
    const handler = (e: Event) => {
      if (
        (e as CustomEvent<{ workspaceId: string }>).detail.workspaceId === wid
      )
        setSessionIsReady(true);
    };
    window.addEventListener("mm-ida-workspace-ready", handler);
    return () => window.removeEventListener("mm-ida-workspace-ready", handler);
  }, [workspace?.id]);

  const settingsNavRef = useTutorialTarget("ida-settings-nav");
  const docTypesNavRef = useTutorialTarget("meridian-doc-types-nav");
  // Gate on sessionIsReady, not isReady. isReady includes workspaceIsReady, which
  // is derived from the workspace having a description — permanently true for every
  // configured workspace, so it fired on every entry into an established workspace.
  // sessionIsReady is set only by the mm-ida-workspace-ready event and cleared on
  // workspace switch, making it the genuine "onboarding just finished" transition
  // this post-init hint was written for.
  useTutorialHint(
    {
      id: "ida-post-init-settings",
      targetId: "ida-settings-nav",
      title: "Configure your workspace",
      body: "Visit Settings to review Module Features — skill offerings, workspace mode, and more.",
      placement: "right",
      scope: "workspace",
    },
    isAdmin && sessionIsReady,
  );

  // Update sidebar width CSS variable (desktop only)
  // CSS variable logic removed - using natural layout flow

  // Function to check if the user has the required role for a navigation item
  const userHasRole = (requiredRole: string | undefined): boolean => {
    if (!requiredRole) return true;

    switch (requiredRole) {
      case "owner":
        return isOwner;
      case "admin":
        return isAdmin;
      case "editor":
        return isEditor;
      case "user":
        return isUser;
      default:
        return false;
    }
  };

  // Active module from the URL (/workspace/:id/<module>/...). Used to surface a
  // replay control for modules that ship a guided tour.
  const activeModuleId = location.pathname.split("/")[3];
  const activeModuleConfig = activeModuleId
    ? moduleRegistry[activeModuleId]
    : undefined;
  const showTourButton = !!activeModuleConfig?.hasTour;

  const handleReplayTour = () => {
    if (!workspace?.id || !activeModuleId || !activeModuleConfig) return;
    // Clear dismissals so a previously completed tour can run again.
    resetAllDismissed();
    // Navigate to the module's tour page (its start page unless the module says
    // otherwise) — for a fresh mount the landing view's
    // auto-start effect runs the tour. If we're already there, the event below
    // triggers an immediate replay on the mounted view.
    const tourPage =
      activeModuleConfig.tourStartPage ?? activeModuleConfig.startPage;
    navigate(`/workspace/${workspace.id}/${activeModuleId}/${tourPage}`);
    window.dispatchEvent(
      new CustomEvent("mm-replay-tour", { detail: { module: activeModuleId } }),
    );
    if (isMobile && onClose) onClose();
  };

  if (!workspace?.id) return null;

  return (
    <Box
      component="aside"
      sx={{
        width: isMobile ? "100%" : isCollapsed ? "64px" : "250px",
        minWidth: isMobile ? "auto" : isCollapsed ? "64px" : "250px",
        maxWidth: isMobile ? "100%" : isCollapsed ? "64px" : "250px",
        // Desktop: fixed so it stays visible while long workspace pages scroll.
        // Mobile: height 100% fills the fixed overlay container.
        position: isMobile ? undefined : "fixed",
        top: isMobile ? undefined : "var(--header-height)",
        left: isMobile ? undefined : 0,
        height: isMobile ? "100%" : "calc(100vh - var(--header-height))",
        zIndex: isMobile ? undefined : 1000,
        backgroundColor: "background.surface",
        borderRight: isMobile ? "none" : "1px solid",
        borderColor: "divider",
        transition: isMobile
          ? "none"
          : "width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        "& *": {
          overflowX: "hidden",
        },
      }}
    >
      {/* Workspace Header */}
      <Box
        sx={{
          p: isMobile ? 2 : isCollapsed ? 1.5 : 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.level1",
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile
            ? "space-between"
            : isCollapsed
              ? "center"
              : "flex-start",
        }}
      >
        {!isCollapsed && workspaceName ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
          >
            <Avatar
              size="sm"
              sx={{
                bgcolor: "primary.500",
                fontSize: "0.75rem",
                flexShrink: 0,
              }}
            >
              <BusinessIcon sx={{ color: "#ffffff" }} />
            </Avatar>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <Typography
                level="body-xs"
                color="neutral"
                sx={{
                  mb: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  transition: "none",
                }}
              >
                Current Workspace
              </Typography>
              <Tooltip title={workspaceName || ""} placement="top">
                <Typography
                  level="body-sm"
                  sx={{
                    fontWeight: "md",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "text.primary",
                    cursor: "default",
                    transition: "none",
                  }}
                >
                  {workspaceName}
                </Typography>
              </Tooltip>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Avatar
              size="sm"
              sx={{
                bgcolor: "primary.500",
                fontSize: "0.75rem",
              }}
            >
              <BusinessIcon sx={{ color: "#ffffff" }} />
            </Avatar>
          </Box>
        )}

        {/* Replay-tour control — shown for modules that ship a guided tour */}
        {showTourButton && !isCollapsed && (
          <Tooltip title="Replay tour" placement="bottom">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={handleReplayTour}
              aria-label="Replay tour"
              sx={{ borderRadius: "50%", flexShrink: 0, ml: 0.5 }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Mobile Close Button */}
        {isMobile && onClose && (
          <IconButton
            size="sm"
            variant="plain"
            onClick={onClose}
            sx={{
              color: "text.primary",
              "&:hover": {
                backgroundColor: "background.level2",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: "auto", minWidth: 0, maxWidth: "100%" }}>
        <List
          size="sm"
          sx={{
            "--ListItem-radius": "8px",
            "--List-gap": "4px",
            p: 1,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <ListItem>
            <ListItemButton
              component={Link}
              to="/"
              onClick={isMobile ? onClose : undefined}
              sx={{
                justifyContent: isCollapsed ? "center" : "flex-start",
                minHeight: "40px",
              }}
            >
              <ArrowBackIcon />
              {!isCollapsed && (
                <Typography
                  sx={{ ml: 1, whiteSpace: "nowrap", transition: "none" }}
                >
                  Workspaces
                </Typography>
              )}
            </ListItemButton>
          </ListItem>

          {/* Dynamic Module Navigation */}
          {enabledModules.map((moduleId) => {
            const moduleConfig = moduleRegistry[moduleId];
            if (!moduleConfig?.navigation) return null;

            return moduleConfig.navigation.map((navItem) => {
              if (navItem.requiredRole && !userHasRole(navItem.requiredRole))
                return null;
              if (navItem.requiredFeature && !flags?.[navItem.requiredFeature])
                return null;

              const isActive =
                location.pathname ===
                `/workspace/${workspace?.id}/${moduleId}/${navItem.path}`;
              const IconComponent = navItem.icon;

              const locked =
                moduleId === "ida" && navItem.path !== "chat" && !isReady;
              const navUrl = `/workspace/${workspace?.id}/${moduleId}/${navItem.path}`;

              return (
                <ListItem
                  key={`${moduleId}-${navItem.path}`}
                  ref={
                    moduleId === "meridian" &&
                    navItem.path === "admin/document-types"
                      ? docTypesNavRef
                      : undefined
                  }
                >
                  <Tooltip
                    title={locked ? "Complete the setup chat first" : ""}
                    placement="right"
                  >
                    {locked ? (
                      <ListItemButton
                        aria-disabled="true"
                        sx={{
                          justifyContent: isCollapsed ? "center" : "flex-start",
                          minHeight: "40px",
                          opacity: 0.45,
                          cursor: "not-allowed",
                        }}
                      >
                        {IconComponent && <IconComponent />}
                        {!isCollapsed && (
                          <Typography
                            sx={{
                              ml: 1,
                              whiteSpace: "nowrap",
                              transition: "none",
                            }}
                          >
                            {navItem.name}
                          </Typography>
                        )}
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        component={Link}
                        to={navUrl}
                        selected={isActive}
                        onClick={isMobile ? onClose : undefined}
                        sx={{
                          justifyContent: isCollapsed ? "center" : "flex-start",
                          minHeight: "40px",
                        }}
                      >
                        {IconComponent && <IconComponent />}
                        {!isCollapsed && (
                          <Typography
                            sx={{
                              ml: 1,
                              whiteSpace: "nowrap",
                              transition: "none",
                            }}
                          >
                            {navItem.name}
                          </Typography>
                        )}
                      </ListItemButton>
                    )}
                  </Tooltip>
                </ListItem>
              );
            });
          })}

          {/* Core Navigation Items */}
          {/* Settings: Visible to Owners and Admins */}
          {isAdmin && (
            <ListItem ref={settingsNavRef}>
              <Tooltip
                title={settingsLocked ? "Complete the setup chat first" : ""}
                placement="right"
              >
                {settingsLocked ? (
                  <ListItemButton
                    aria-disabled="true"
                    sx={{
                      justifyContent: isCollapsed ? "center" : "flex-start",
                      minHeight: "40px",
                      opacity: 0.45,
                      cursor: "not-allowed",
                    }}
                  >
                    <SettingsIcon />
                    {!isCollapsed && (
                      <Typography
                        sx={{ ml: 1, whiteSpace: "nowrap", transition: "none" }}
                      >
                        Settings
                      </Typography>
                    )}
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    component={Link}
                    to={`/workspace/${workspace?.id}/settings`}
                    selected={
                      location.pathname ===
                      `/workspace/${workspace?.id}/settings`
                    }
                    onClick={isMobile ? onClose : undefined}
                    sx={{
                      justifyContent: isCollapsed ? "center" : "flex-start",
                      minHeight: "40px",
                    }}
                  >
                    <SettingsIcon />
                    {!isCollapsed && (
                      <Typography
                        sx={{ ml: 1, whiteSpace: "nowrap", transition: "none" }}
                      >
                        Settings
                      </Typography>
                    )}
                  </ListItemButton>
                )}
              </Tooltip>
            </ListItem>
          )}

          {/* Users: Visible to Admins and Owners */}
          {isAdmin && (
            <ListItem>
              <ListItemButton
                component={Link}
                to={`/workspace/${workspace?.id}/users`}
                selected={
                  location.pathname === `/workspace/${workspace?.id}/users`
                }
                onClick={isMobile ? onClose : undefined}
                sx={{
                  justifyContent: isCollapsed ? "center" : "flex-start",
                  minHeight: "40px",
                }}
              >
                <PeopleAltIcon />
                {!isCollapsed && (
                  <Typography
                    sx={{ ml: 1, whiteSpace: "nowrap", transition: "none" }}
                  >
                    Users
                  </Typography>
                )}
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>
    </Box>
  );
};

export default WorkspaceSidebar;
